<template>
  <div v-if="showSearchForm" class="bg-gray-50 rounded-lg p-4 sm:p-6 sm:pb-4 mb-4 -mt-2 relative">
    <AForm
      :state
      class="flex flex-col sm:flex-row items-stretch gap-3"
      :show-submit="false"
    >
      <UFormGroup
        :label="$t('components.resources.trips.searchLabel')"
        class="w-full sm:basis-1/2"
      >
        <UInput
          name="search"
          size="xl"
          :placeholder="$t('components.resources.trips.searchPlaceholder')"
          :model-value="state.search"
          @update:model-value="updateSearch"
        />
      </UFormGroup>
      <UFormGroup
        :label="$t('components.resources.trips.filterByDestination')"
        class="w-full sm:basis-1/2"
      >
        <ATagList
          v-model="state.destinations"
          searchable="/api/destinations/select"
          name="destinations"
          :options="destinationsData!"
          keep-bindings-as-object
          :placeholder="
            $t('components.resources.trips.filterByDestinationPlaceholder')
          "
          multiple
          size="xl"
          option-attribute="label"
          value-attribute="value"
        />
      </UFormGroup>
    </AForm>
    <UButton
      v-if="state.search || state.destinations.length > 0"
      class="mt-4 sm:mt-0"
      :padded="false"
      color="red"
      variant="link"
      leading-icon="mdi:close"
      @click="clearFilters"
    >
      {{ $t("components.resources.trips.reset") }}
    </UButton>
  </div>
  <CategoriesCarousel v-if="showCategories" class="mb-8" />
  <APaginateItems v-model="page" :items-meta="trips?.meta">
    <div
      v-if="trips!.data.length > 0"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8"
    >
      <template v-if="status !== 'success'">
        <USkeleton v-for="i in limit" :key="i" class="h-[210px] rounded-lg" />
      </template>
      <template v-else-if="trips">
        <TripItem v-for="trip in trips.data" :key="trip.id" :trip="trip" />
      </template>
    </div>
    <div v-else class="text-center text-gray-500 text-xl mt-4 font-semibold">
      {{ $t("components.resources.trips.noTripsFound") }}
    </div>
  </APaginateItems>
</template>

<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core'

const props = withDefaults(defineProps<{
  showSearchForm?: boolean
  showCategories?: boolean
  destinationId?: number
  limit?: number
}>(), {
  showSearchForm: true,
  showCategories: true,
  limit: 20,
})
const emit = defineEmits<{
  fetched: [trips: any[]]
}>()
const router = useRouter()
const route = useRoute()
const page = ref(route.query.page ? Number(route.query.page) : 1)

const { data: destinationsData, error: destinationsDataError } = await useFetch(
  '/api/destinations/select',
  {
    query: {
      hasTrips: 1,
    },
  },
)
assertDefined(destinationsData, destinationsDataError)

const state = ref({
  destinations: destinationsData.value!.filter(d =>
    route.query.dest?.includes(d.value.toString()),
  ),
  search: route.query.search?.toString() ?? '',
})

const updateSearch = useDebounceFn((term: string) => {
  state.value.search = term
}, 500)

const {
  data: trips,
  error: tripsError,
  status,
} = await useFetch('/api/trips/paginate', {
  query: {
    category: computed(() => route.params.category),
    destinations: computed(() => props.destinationId ?? state.value.destinations.map(d => d.value)),
    // ATagList prevents using a single computed containing all the values
    // because it triggers a re-render when the state is updated
    page: computed(() => page.value),
    search: computed(() => state.value.search),
    limit: props.limit,
  },
})
assertDefined(trips, tripsError)

emit('fetched', trips.value.data)

function clearFilters() {
  state.value.search = ''
  state.value.destinations = []
}

watch(
  state,
  (newState) => {
    router.push({
      query: {
        dest: newState.destinations.map(d => d.value),
        search: newState.search || undefined,
      },
    })
  },
  { deep: true },
)
</script>
